<template>
  <Transition v-if="isLoading" name="spinner">
    <div id="cover-spin">
      <span class="loader">
        <span>{{ $spinnerText }}</span>
      </span>
    </div>
  </Transition>

  <v-form :name="props.formProps.formName">
    <Tabs value="0" scrollable>

      <TabList class="mb-4">
        <Tab value="0">{{ __('Basic data') }}</Tab>
        <Tab value="1">{{ __('Changelog') }}</Tab>
      </TabList>

      <TabPanels>
        <TabPanel value="0">
          <div class="grid">
            <FormInputText fieldName="conversion_name" label="Conversion name" validation="required" :isFocus="true"
              :isFormDisabled="isFormDisabled" v-model:field="crud.conversion_name" />
          </div>
          <div v-if="crud.started_at" class="grid">
            <FormInputText fieldName="started_at" label="Started at" size="6" :isFormDisabled="true"
              v-model:field="crud.started_at" />

            <FormInputText fieldName="finished_at" label="Finished at" size="6" :isFormDisabled="true"
              v-model:field="crud.finished_at" />
          </div>
          <div v-if="crud.output_message" class="grid">
            <FormTextarea fieldName="output_message" label="Output message" rows="3" :isFormDisabled="true"
              v-model:field="crud.output_message" />
          </div>
          <div class="grid">
            <FormFileUploader fieldName="fileToConversion" label="Files to conversion" category="excel"
              :isShortNames="true" :allowedExtensions="['xlsx', 'xls', 'csv']" :style="{ minWidth: '200px' }"
              :isFormDisabled="isFormDisabled" v-model:field="crud.files" />

            <FormFileUploader v-if="isConvertedFileExists" label="Converted files" category="epp" :maxFiles="0"
              :isShortNames="true" :allowedExtensions="['xlsx', 'xls', 'csv']" :style="{ minWidth: '200px' }"
              :isFormDisabled="isFormDisabled" v-model:field="crud.files" />

          </div>
        </TabPanel>

        <TabPanel v-if="crud.log" value="1" :pt="{ root: { class: 'form-tab-panel' } }">
          <ChangeLog :log="crud?.log" />
        </TabPanel>
      </TabPanels>
    </Tabs>

    <div class="grid">
      <FormButtons :formProps="props.formProps" :sourceForm="crud" :validation="validation"
        :buttonsVisibility="buttonsVisibility" :showAfterSave="true" :afterSave="afterSave"
        v-model:isFormDisabled="isFormDisabled">
        <template #after-in-show="buttonProps">
          <CustomButton name="convert" :buttonProps="buttonProps" :buttonsVisibility="buttonsVisibility"
            :click="() => convert(buttonProps.saveData)" style="margin:0 5px" severity="info">
            {{ __('Convert') }}
          </CustomButton>
        </template>
      </FormButtons>
    </div>
  </v-form>
</template>

<script setup>
import { ref, onMounted } from "vue";
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';

import { validation, initForm } from "@pages/Helper/crud_utils.js";
import FormInputText from "@crud/FormInputText.vue";
import FormButtons from "@crud/FormButtons.vue";
import CustomButton from "@crud/CustomButton.vue";
import ChangeLog from "@crud/ChangeLog.vue";
import FormStaticText from "@crud/FormStaticText.vue";
import FormTextarea from "@crud/FormTextarea.vue";
import FormFileUploader from "@crud/FormFileUploader.vue";
import { __, screenMessage, getFromRoute, download, removeDuplicates, removeNonNumericKeys } from "@pages/Helper/utils.js";

// -----------------------------------------------------

const props = defineProps({
  formProps: Object,
});

const isLoading = ref(true);
const isFormDisabled = ref(false);
const isConvertedFileExists = ref(false);

const crud = ref({});
const buttonsVisibility = ref({});

// -----------------------------------------------------

onMounted(async () => {
  await initForm(props, crud, isLoading, null, null, buttonsVisibility);
  checkIfConvertedFileExists(crud.value.files);
});

// -------------------------------------------------------------------------------------------------

const convert = async () => {
  isLoading.value = true;

  const response = await getFromRoute('conversion-make', { 'conversion_id': crud.value.id }, 'get')

  if (response?.status === 'success') {
    screenMessage({ message: __('Conversion completed successfully'), title: __('Success'), type: 'success', position: 'center', timeout: 5 });

    const cleanedFiles = removeNonNumericKeys(crud.value.files);
    crud.value.files = removeDuplicates([...cleanedFiles, ...response.convertedFiles], 'file_name');
    checkIfConvertedFileExists([...response.convertedFiles]);

    for (const fileObj of response.convertedFiles) {
      await download(fileObj?.file_path);
    }
  } else {
    screenMessage({ message: __('Conversion failed'), title: __('Error'), type: 'error', position: 'center', timeout: 10 });
  }

  isLoading.value = false;
};

// -------------------------------------------------------------------------------------------------

const checkIfConvertedFileExists = (files) => {
  if (!files) {
    isConvertedFileExists.value = false;
    return false;
  }

  if (files.hasOwnProperty('epp') && Array.isArray(files['epp']) && files['epp'].length == 0) {
    isConvertedFileExists.value = false;
    return false;
  }

  const cleanedFiles = removeNonNumericKeys(crud.value.files);

  for (const fileObj of cleanedFiles) {
    if (fileObj?.file_category === 'epp') {
      isConvertedFileExists.value = true;
      return true;
    }
  }

  isConvertedFileExists.value = false;
  return false;
};

// -------------------------------------------------------------------------------------------------

const afterSave = () => {
  checkIfConvertedFileExists(crud.value.files);
};

// -------------------------------------------------------------------------------------------------

</script>

<style lang="scss" scoped></style>
